import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { Plus, PlusCircle, Pencil, Trash2, Filter, Search, ChevronDown, ChevronUp, Loader2, Tag, Zap, Palette } from 'lucide-react'
import {
  Card,
  CardContent,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Badge,
  Separator,
} from '@jira-clone/ui'
import { useProductServices, useProductServiceMutations } from '../../shared/hooks/useProductServices'
import { useHsnSacCodes, useHsnSacCodeMutations } from '../../shared/hooks/useHsnSacCodes'
import { useUnitMasters } from '../../shared/hooks/useUnitMasters'
import { useTaxMasters, taxLabel } from '../../shared/hooks/useTaxMasters'
import { getApiErrorMessage } from '../../shared/utils/apiError'

const CATEGORY_COLORS: Record<string, string> = {
  TECHNOLOGY: '#6366f1',
  MARKETING_GROWTH: '#ec4899',
  CREATIVE: '#f59e0b',
}

const CATEGORY_LABELS: Record<string, string> = {
  TECHNOLOGY: 'Technology',
  MARKETING_GROWTH: 'Marketing & Growth',
  CREATIVE: 'Creative',
}

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  TECHNOLOGY: <Zap className="h-3.5 w-3.5" />,
  MARKETING_GROWTH: <Tag className="h-3.5 w-3.5" />,
  CREATIVE: <Palette className="h-3.5 w-3.5" />,
}

interface DraftState {
  id?: string
  name: string
  description: string
  sku: string
  hsnSacId: string
  category: 'TECHNOLOGY' | 'MARKETING_GROWTH' | 'CREATIVE'
  defaultRate: number
  baseRate: number
  currency: string
  unitId: string
  taxMasterId: string
  taxability: 'taxable' | 'exempt' | 'nil'
  isActive: boolean
}

const EMPTY_DRAFT: DraftState = {
  name: '',
  description: '',
  sku: '',
  hsnSacId: '',
  category: 'TECHNOLOGY',
  defaultRate: 0,
  baseRate: 0,
  currency: 'INR',
  unitId: '',
  taxMasterId: '',
  taxability: 'taxable',
  isActive: true,
}

export default function ProductServicesAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const categoryParam = searchParams.get('category') as 'TECHNOLOGY' | 'MARKETING_GROWTH' | 'CREATIVE' | 'all' | null
  const initialCategory = categoryParam && ['TECHNOLOGY', 'MARKETING_GROWTH', 'CREATIVE', 'all'].includes(categoryParam)
    ? categoryParam
    : 'all'
  const [activeCategory, setActiveCategoryState] = useState(initialCategory)
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [isActiveFilter, setIsActiveFilter] = useState<'all' | 'true' | 'false'>(searchParams.get('isActive') as any || 'all')

  useEffect(() => {
    if (categoryParam && ['TECHNOLOGY', 'MARKETING_GROWTH', 'CREATIVE', 'all'].includes(categoryParam)) {
      setActiveCategoryState(categoryParam)
    } else if (!categoryParam) {
      setActiveCategoryState('all')
    }
  }, [categoryParam])

  const handleCategoryChange = (cat: 'TECHNOLOGY' | 'MARKETING_GROWTH' | 'CREATIVE' | 'all') => {
    setActiveCategoryState(cat)
    setSearchParams(cat === 'all' ? {} : { category: cat })
  }

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setSearchParams(value ? { search: value, ...(activeCategory !== 'all' ? { category: activeCategory } : {}) } : 
      activeCategory !== 'all' ? { category: activeCategory } : {})
  }

  const handleIsActiveChange = (value: 'all' | 'true' | 'false') => {
    setIsActiveFilter(value)
    setSearchParams(value === 'all' ? { ...(activeCategory !== 'all' ? { category: activeCategory } : {}) } : 
      { isActive: value, ...(activeCategory !== 'all' ? { category: activeCategory } : {}) })
  }

  const { data: productServices = [], isLoading } = useProductServices({
    category: activeCategory === 'all' ? undefined : activeCategory,
    isActive: isActiveFilter === 'all' ? undefined : isActiveFilter === 'true',
    search: search || undefined,
  })
  
  const { create, update, setStatus, setAllStatus, remove } = useProductServiceMutations()
  const { data: hsnSacCodes = [] } = useHsnSacCodes({ isActive: true })
  const { create: createHsnSac } = useHsnSacCodeMutations()
  const [hsnSacCreateOpen, setHsnSacCreateOpen] = useState(false)
  const [hsnSacForm, setHsnSacForm] = useState({ code: '', description: '', type: 'HSN' as 'HSN' | 'SAC' })
  const { data: unitMasters = [] } = useUnitMasters({ isActive: true })
  const { data: taxMasters = [] } = useTaxMasters({ isActive: true })

  const [dialogOpen, setDialogOpen] = useState(false)
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  // Base Rate is the discount floor for this service in the sales flow — never above the Default Rate.
  const baseRateInvalid = draft.baseRate > draft.defaultRate

  const openCreate = () => {
    setDraft(EMPTY_DRAFT)
    setDialogOpen(true)
  }

  const openEdit = (service: any) => {
    const toId = (val: any): string => {
      if (!val) return ''
      if (typeof val === 'string') return val
      return val._id?.toString() || val.id?.toString() || ''
    }
    setDraft({
      id: service.id,
      name: service.name,
      description: service.description || '',
      sku: service.sku || '',
      hsnSacId: toId(service.hsnSacId),
      category: service.category,
      defaultRate: service.defaultRate || 0,
      baseRate: Number(service.baseRate) || 0,
      currency: service.currency || 'INR',
      unitId: toId(service.unitId),
      taxMasterId: toId(service.taxMasterId),
      taxability: service.taxability || 'taxable',
      isActive: service.isActive,
    })
    setDialogOpen(true)
  }

  const handleSave = async () => {
    if (!draft.name.trim()) {
      toast.error('Service name is required.')
      return
    }
    try {
      const payload = {
        name: draft.name.trim(),
        description: draft.description.trim() || undefined,
        sku: draft.sku.trim() || undefined,
        hsnSacId: draft.hsnSacId || null,
        category: draft.category,
        defaultRate: draft.defaultRate,
        baseRate: draft.baseRate,
        currency: draft.currency,
        unitId: draft.unitId || null,
        taxMasterId: draft.taxMasterId || null,
        taxability: draft.taxability,
        isActive: draft.isActive,
      }
      if (draft.id) {
        await update.mutateAsync({ id: draft.id, payload })
        toast.success('Service updated.')
      } else {
        await create.mutateAsync(payload)
        toast.success('Service created.')
      }
      setDialogOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, draft.id ? 'update this service' : 'create this service'))
    }
  }

  const handleDelete = async (service: any) => {
    if (!confirm(`Delete "${service.name}"? This can't be undone.`)) return
    setDeletingId(service.id)
    try {
      await remove.mutateAsync(service.id)
      toast.success('Service deleted.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete this service'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleToggleActive = async (service: any) => {
    try {
      await setStatus.mutateAsync({ id: service.id, isActive: !service.isActive })
      toast.success(service.isActive ? 'Service deactivated.' : 'Service activated.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'toggle service status'))
    }
  }

  const handleSetAllActive = async (isActive: boolean) => {
    const verb = isActive ? 'Enable' : 'Disable'
    if (!confirm(`${verb} ALL services? ${isActive ? 'They will appear in dropdowns again.' : 'They will disappear from every service dropdown until re-enabled.'}`)) return
    try {
      const res = await setAllStatus.mutateAsync(isActive)
      toast.success(`${res?.modified ?? 0} service(s) ${isActive ? 'enabled' : 'disabled'}.`)
    } catch (err) {
      toast.error(getApiErrorMessage(err, `${verb.toLowerCase()} all services`))
    }
  }

  const handleInlineHsnSacCreate = async () => {
    if (!hsnSacForm.code.trim()) {
      toast.error('HSN/SAC code is required.')
      return
    }
    try {
      const result = await createHsnSac.mutateAsync({ ...hsnSacForm, isActive: true } as any)
      setDraft((d) => ({ ...d, hsnSacId: (result as any).id }))
      setHsnSacCreateOpen(false)
      setHsnSacForm({ code: '', description: '', type: 'HSN' })
      toast.success('HSN/SAC code added.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'add HSN/SAC code'))
    }
  }

  const categories: ('all' | 'TECHNOLOGY' | 'MARKETING_GROWTH' | 'CREATIVE')[] = ['all', 'TECHNOLOGY', 'MARKETING_GROWTH', 'CREATIVE']

  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">Products & Services</h2>
          <p className="text-sm text-muted-foreground">Manage the products and services offered by the company.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => handleSetAllActive(false)} disabled={setAllStatus.isPending}>
            Disable all
          </Button>
          <Button variant="outline" onClick={() => handleSetAllActive(true)} disabled={setAllStatus.isPending}>
            Enable all
          </Button>
          <Button onClick={openCreate} className="gap-1.5">
            <Plus className="h-4 w-4" /> New Service
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Category Tabs */}
            <div className="flex items-center gap-1 bg-muted p-1 rounded-lg">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => handleCategoryChange(cat)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    activeCategory === cat
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {cat !== 'all' && <span style={{ color: CATEGORY_COLORS[cat] }}>{CATEGORY_ICONS[cat]}</span>}
                  {cat === 'all' ? 'All' : CATEGORY_LABELS[cat]}
                </button>
              ))}
            </div>

            <Separator className="h-6 w-px" />

            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search services..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Active Filter */}
            <Select value={isActiveFilter} onValueChange={handleIsActiveChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : productServices.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              No services yet. Create the first one.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {productServices.map((service) => (
            <Card key={service.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${CATEGORY_COLORS[service.category]}20`, color: CATEGORY_COLORS[service.category] }}
                      >
                        {CATEGORY_ICONS[service.category]}
                      </span>
                      <Badge variant={service.isActive ? 'default' : 'secondary'} className="text-xs">
                        {service.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-foreground truncate">{service.name}</p>
                        {service.sku && (
                          <span className="text-xs text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">
                            {service.sku}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate flex items-center gap-2">
                        {service.description && <span>{service.description}</span>}
                        {service.hsnSac && <span>HSN/SAC: {service.hsnSac.code}</span>}
                        {service.unit && <span>Unit: {service.unit.displayName}</span>}
                        {service.taxMaster && <span>Tax: {service.taxMaster.name} ({service.taxMaster.rate}%)</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(service)} title="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleActive(service)}
                      title={service.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {service.isActive ? (
                        <span className="h-3.5 w-3.5 rounded-full bg-green-500" />
                      ) : (
                        <span className="h-3.5 w-3.5 rounded-full bg-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(service)}
                      disabled={deletingId === service.id}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{draft.id ? 'Edit Service' : 'New Service'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="service-name">Name *</Label>
                <Input
                  id="service-name"
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  placeholder="e.g. Web Development"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="service-category">Category *</Label>
                <Select value={draft.category} onValueChange={(v) => setDraft((d) => ({ ...d, category: v as any }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TECHNOLOGY">Technology</SelectItem>
                    <SelectItem value="MARKETING_GROWTH">Marketing & Growth</SelectItem>
                    <SelectItem value="CREATIVE">Creative</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="service-sku">SKU</Label>
                <Input
                  id="service-sku"
                  value={draft.sku}
                  onChange={(e) => setDraft((d) => ({ ...d, sku: e.target.value.toUpperCase() }))}
                  placeholder="Optional SKU code"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="service-rate">Default Rate (₹)</Label>
                <Input
                  id="service-rate"
                  type="number"
                  min={0}
                  step={0.01}
                  value={draft.defaultRate}
                  onChange={(e) => setDraft((d) => ({ ...d, defaultRate: isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="service-base-rate">Base Rate (₹)</Label>
                <Input
                  id="service-base-rate"
                  type="number"
                  min={0}
                  step={0.01}
                  value={draft.baseRate}
                  onChange={(e) => setDraft((d) => ({ ...d, baseRate: isNaN(parseFloat(e.target.value)) ? 0 : parseFloat(e.target.value) || 0 }))}
                />
                <p className="text-[11px] text-muted-foreground">Minimum selling rate allowed after discount</p>
                {baseRateInvalid && (
                  <p className="text-[11px] font-medium text-red-600">Base Rate cannot be greater than Default Rate.</p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="service-description">Description</Label>
              <textarea
                id="service-description"
                rows={3}
                value={draft.description}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                placeholder="Describe the service in detail — scope, inclusions, use cases…"
                className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500 dark:text-slate-100 transition-colors resize-y"
              />
            </div>

            <Separator className="my-2" />

            <h4 className="text-sm font-medium text-foreground">Tax & Compliance</h4>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="service-hsn">HSN/SAC Code</Label>
                <Select
                  value={draft.hsnSacId}
                  onValueChange={(v) => {
                    if (v === '__add_new__') {
                      setHsnSacCreateOpen(true)
                    } else {
                      setDraft((d) => ({ ...d, hsnSacId: v }))
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select HSN/SAC" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {hsnSacCodes.map((code) => (
                      <SelectItem key={code.id} value={code.id}>
                        {code.code} - {code.description}
                      </SelectItem>
                    ))}
                    <SelectItem value="__add_new__">
                      <span className="flex items-center gap-1.5 text-primary">
                        <PlusCircle className="h-3.5 w-3.5" /> Add new code...
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="service-unit">Unit</Label>
                <Select value={draft.unitId} onValueChange={(v) => setDraft((d) => ({ ...d, unitId: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {unitMasters.map((unit) => (
                      <SelectItem key={unit.id} value={unit.id}>
                        {unit.displayName} ({unit.shortCode})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="service-tax">Tax Master</Label>
                <Select value={draft.taxMasterId} onValueChange={(v) => setDraft((d) => ({ ...d, taxMasterId: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Tax" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {taxMasters.map((tax) => (
                      <SelectItem key={tax.id} value={tax.id}>
                        {taxLabel(tax)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="service-taxability">Taxability</Label>
                <Select value={draft.taxability} onValueChange={(v) => setDraft((d) => ({ ...d, taxability: v as any }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="taxable">Taxable</SelectItem>
                    <SelectItem value="exempt">Exempt</SelectItem>
                    <SelectItem value="nil">Nil Rated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="service-currency">Currency</Label>
                <Input
                  id="service-currency"
                  value={draft.currency}
                  onChange={(e) => setDraft((d) => ({ ...d, currency: e.target.value }))}
                  placeholder="INR"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.checked }))}
                />
                <span>Active</span>
              </Label>
            </div>

            <Dialog open={hsnSacCreateOpen} onOpenChange={setHsnSacCreateOpen}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Add HSN/SAC Code</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="hsn-sac-code">Code *</Label>
                    <Input
                      id="hsn-sac-code"
                      value={hsnSacForm.code}
                      onChange={(e) => setHsnSacForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                      placeholder="e.g. 998313"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="hsn-sac-type">Type</Label>
                    <Select value={hsnSacForm.type} onValueChange={(v) => setHsnSacForm((f) => ({ ...f, type: v as 'HSN' | 'SAC' }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="HSN">HSN</SelectItem>
                        <SelectItem value="SAC">SAC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="hsn-sac-desc">Description *</Label>
                    <Input
                      id="hsn-sac-desc"
                      value={hsnSacForm.description}
                      onChange={(e) => setHsnSacForm((f) => ({ ...f, description: e.target.value }))}
                      placeholder="e.g. Design & Creative Services"
                    />
                  </div>
                </div>
                <DialogFooter className="mt-4">
                  <Button variant="outline" onClick={() => setHsnSacCreateOpen(false)}>Cancel</Button>
                  <Button onClick={handleInlineHsnSacCreate} disabled={createHsnSac.isPending}>
                    {createHsnSac.isPending ? 'Adding...' : 'Add Code'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={create.isPending || update.isPending || baseRateInvalid}>
              {draft.id ? 'Save Changes' : 'Create Service'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}